/**
 * @file Admin dashboard UI — pages/Dashboard/Admin/PublicPages/AdminPublicPagesManager.tsx.
 */

import React, { useEffect, useMemo, useState } from "react";
import Button from "../../../../components/common/Button";
import OutlineButton from "../../../../components/common/OutlineButton";
import RichTextEditor from "../../../../components/common/RichTextEditor";
import { adminPageService, type AdminPage } from "../../../../services/adminPageService";
import type { PublicPageSection } from "../../../../services/publicPageService";
import "../Admin.css";
import "./AdminPublicPagesManager.css";

type PageOption = { slug: string; label: string };

/** React component — Admin Public Pages Manager. */
export default function AdminPublicPagesManager() {
  const pages: PageOption[] = useMemo(
    () => [
      { slug: "cookies-policy", label: "Cookies Policy" },
      { slug: "privacy-policy", label: "Privacy Policy" },
      { slug: "legal", label: "Legal" },
      { slug: "terms-of-use", label: "Terms of use" },
    ],
    []
  );

  const [slug, setSlug] = useState<PageOption["slug"]>(pages[0].slug);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [title, setTitle] = useState("");
  const [sections, setSections] = useState<PublicPageSection[]>([{ body: "" }]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      setError(null);
      setSuccess(null);
      try {
        const page: AdminPage | null = await adminPageService.getBySlug(slug);
        if (!mounted) return;

        setTitle(page?.title || pages.find((p) => p.slug === slug)?.label || "");
        if (page?.sections?.length) {
          setSections(page.sections);
        } else if (page?.content) {
          const paras = String(page.content)
            .split(/\n{2,}/g)
            .map((p) => p.trim())
            .filter(Boolean);
          setSections(paras.length ? paras.map((p) => ({ body: p })) : [{ body: "" }]);
        } else {
          setSections([{ body: "" }]);
        }
      } catch (e: any) {
        if (!mounted) return;
        setTitle(pages.find((p) => p.slug === slug)?.label || "");
        setSections([{ body: "" }]);
        setError(
          e?.response?.data?.message ||
            e?.message ||
            "Failed to load page content. (Backend API may not be implemented yet.)"
        );
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => {
      mounted = false;
    };
  }, [slug, pages]);

  const canSave = useMemo(() => {
    if (loading || saving) return false;
    if (!title.trim()) return false;
    const nonEmpty = sections.some((s) => String(s.body || "").trim().length > 0);
    return nonEmpty;
  }, [loading, saving, title, sections]);

  const onAddSection = () => {
    setSections((prev) => [...prev, { body: "" }]);
  };

  const onRemoveSection = (idx: number) => {
    setSections((prev) => prev.filter((_, i) => i !== idx));
  };

  const onUpdateSection = (idx: number, patch: Partial<PublicPageSection>) => {
    setSections((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const handleSave = async () => {
    setError(null);
    setSuccess(null);
    try {
      setSaving(true);
      const cleaned = sections
        .map((s) => ({
          heading: s.heading ? String(s.heading).trim() : undefined,
          body: String(s.body || "").trim(),
        }))
        .filter((s) => s.body.length > 0);

      await adminPageService.upsert(slug, { title: title.trim(), sections: cleaned });
      setSuccess("Saved.");
    } catch (e: any) {
      setError(
        e?.response?.data?.message || e?.message || "Failed to save. Please try again."
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="background-form-container analysis-shell">
      <div className="analysis-banner">
        <span className="analysis-banner__title">Public Pages</span>
      </div>

      <div className="admin-form-root">
        <div className="admin-pages-container dataset-container">
          <div className="dataset-section">
            <h3 className="dataset-section-title">Page Settings</h3>
            <div className="dataset-grid">
              <div className="form-field">
                <label>Select page</label>
                <select
                  value={slug}
                  onChange={(e) => setSlug(e.target.value)}
                  disabled={loading || saving}
                  style={{ padding: "8px 10px", border: "1px solid #d1d5db", borderRadius: 8 }}
                >
                  {pages.map((p) => (
                    <option key={p.slug} value={p.slug}>
                      {p.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="form-field" style={{ display: "flex", alignItems: "flex-end" }}>
                <OutlineButton
                  label={saving ? "Saving..." : "Save"}
                  onClick={handleSave}
                  disabled={!canSave}
                  type="button"
                />
              </div>
            </div>
          </div>

          {loading ? (
            <div className="dataset-section">
              <div className="admin-pages-hint">Loading…</div>
            </div>
          ) : (
            <div className="dataset-section">
              <h3 className="dataset-section-title">Page Content</h3>
              <div className="dataset-grid">
                <div className="form-field">
                  <label>Title</label>
                  <input
                    value={title}
                    onChange={(e) => setTitle(e.target.value)}
                    placeholder="Page title"
                    disabled={saving}
                  />
                </div>
              </div>
              <div style={{ marginTop: 16, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                <h4 className="sub-stage" style={{ margin: 0 }}>Sections</h4>
                <Button label="Add section" onClick={onAddSection} type="button" variant="default" />
              </div>
              {sections.map((s, idx) => (
                <div key={idx} className="admin-pages-section-item" style={{ marginTop: 12, padding: 12, background: "#f9fafb", borderRadius: 8, border: "1px solid #e5e5e5" }}>
                  <div className="dataset-grid">
                    <div className="form-field">
                      <label>Heading (optional)</label>
                      <input
                        value={s.heading || ""}
                        onChange={(e) => onUpdateSection(idx, { heading: e.target.value })}
                        placeholder="e.g. What are cookies?"
                        disabled={saving}
                      />
                    </div>
                  </div>
                  <div className="form-field" style={{ marginTop: 10 }}>
                    <label>Body</label>
                    <RichTextEditor
                      value={s.body}
                      onChange={(html) => onUpdateSection(idx, { body: html })}
                      disabled={saving}
                      placeholder="Write content…"
                    />
                  </div>
                  <div style={{ marginTop: 8 }}>
                    <OutlineButton
                      label="Remove"
                      onClick={() => onRemoveSection(idx)}
                      disabled={saving || sections.length <= 1}
                      type="button"
                    />
                  </div>
                </div>
              ))}
              <div className="admin-pages-hint" style={{ marginTop: 12, fontSize: 12, color: "#666" }}>
                Notes: this editor saves basic HTML (bold/italic/lists/links). The backend should sanitize HTML before storing/serving.
              </div>
              {error && <div className="admin-pages-error">{error}</div>}
              {success && <div className="admin-pages-success">{success}</div>}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

